import React from 'react';

export interface PokemonData {
  name: string;
  item: string;
  gender?: 'M' | 'F';
  imageUrl: string;
  details: React.ReactNode[];
  rawDetails: string[];
  moves: string[];
  ability: string;
  level: string;
}

export interface InfoSection {
  type: 'blitzMechanic' | 'bossTip';
  content: React.ReactNode[];
  rawText?: string;
}

export interface TrainerData {
  id: string;
  displayName: string;
  trainerKey: string;
  stage: string; // 'gym-1', ..., 'gym-8', 'e4', 'champion'
  color?: string;
  items: string;
  infoSections: InfoSection[];
  generalInfo: React.ReactNode[];
  pokemon: PokemonData[];
}

export const gymLeaders = [
  'VIOLA', 'ROXANNE', 'BRAWLY', 'WATTSON',
  'FLANNERY', 'NORMAN', 'WINONA', 'TATE AND LIZA', 'JUAN AND WALLACE'
];

export const e4Members = [
  'SIDNEY', 'SPENSER', 'PHOEBE', 'LUCY', 'GLACIA', 'TUCKER', 'DRAKE', 'BRANDON'
];

export const trainerTypeColors: Record<string, string> = {
  'VIOLA': '#9ACD32',      // Bug
  'ROXANNE': '#B8A038',    // Rock
  'BRAWLY': '#C03028',     // Fighting
  'WATTSON': '#F8D030',    // Electric
  'FLANNERY': '#F08030',   // Fire
  'NORMAN': '#A8A875',     // Normal
  'WINONA': '#A890F0',     // Flying
  'TATE&LIZA': '#705898',  // Psychic
  'TATE AND LIZA': '#705898',
  'JUAN': '#6890F0',       // Water
  'JUAN&WALCE': '#6890F0', // Water
  'JUAN AND WALLACE': '#6890F0',
  'SIDNEY': '#705848',     // Dark
  'PHOEBE': '#705898',     // Ghost
  'GLACIA': '#98D8D8',     // Ice
  'DRAKE': '#7038F8',      // Dragon
  'STEVEN': '#B8B8D0',     // Steel
  'SPENSER': '#78C850',    // Grass
  'LUCY': '#A040A0',       // Poison
  'TUCKER': '#EE99AC',     // Fairy
  'BRANDON': '#E0C068',    // Ground
  'WALLY': '#3CB371',      // Wally Green
  'ARCHIE': '#2563EB',     // Aqua
  'MAXIE': '#DC2626',      // Magma
};

export const GYM_MAP_TO_LEADER: Record<string, { trainerKey: string; displayName: string; isE4?: boolean; isChampion?: boolean }> = {
  'RustboroCity_Gym': { trainerKey: 'ROXANNE', displayName: 'Roxanne' },
  'RustboroCity_Gym_Viola_Edition': { trainerKey: 'VIOLA', displayName: 'Viola' },
  'DewfordTown_Gym': { trainerKey: 'BRAWLY', displayName: 'Brawly' },
  'MauvilleCity_Gym': { trainerKey: 'WATTSON', displayName: 'Wattson' },
  'LavaridgeTown_Gym_1F': { trainerKey: 'FLANNERY', displayName: 'Flannery' },
  'LavaridgeTown_Gym_B1F': { trainerKey: 'FLANNERY', displayName: 'Flannery' },
  'PetalburgCity_Gym': { trainerKey: 'NORMAN', displayName: 'Norman' },
  'FortreeCity_Gym': { trainerKey: 'WINONA', displayName: 'Winona' },
  'MossdeepCity_Gym': { trainerKey: 'TATE&LIZA', displayName: 'Tate & Liza' },
  'SootopolisCity_Gym_1F': { trainerKey: 'JUAN&WALCE', displayName: 'Juan & Wallace' },
  'SootopolisCity_Gym_B1F': { trainerKey: 'JUAN&WALCE', displayName: 'Juan & Wallace' },

  // Elite Four
  'EverGrandeCity_SidneysRoom': { trainerKey: 'SIDNEY', displayName: 'Sidney', isE4: true },
  'EverGrandeCity_PhoebesRoom': { trainerKey: 'PHOEBE', displayName: 'Phoebe', isE4: true },
  'EverGrandeCity_GlaciasRoom': { trainerKey: 'GLACIA', displayName: 'Glacia', isE4: true },
  'EverGrandeCity_DrakesRoom': { trainerKey: 'DRAKE', displayName: 'Drake', isE4: true },
  'EverGrandeCity_SpensersRoom': { trainerKey: 'SPENSER', displayName: 'Spenser', isE4: true },
  'EverGrandeCity_LucysRoom': { trainerKey: 'LUCY', displayName: 'Lucy', isE4: true },
  'EverGrandeCity_BrandonsRoom': { trainerKey: 'BRANDON', displayName: 'Brandon', isE4: true },
  'EverGrandeCity_TuckersRoom': { trainerKey: 'TUCKER', displayName: 'Tucker', isE4: true },
  'EverGrandeCity_ChampionsRoom': { trainerKey: 'STEVEN', displayName: 'Steven / Wally', isChampion: true },
};

export interface DetectedBoss {
  trainerKey: string;
  displayName: string;
  stage: string;
  gymNumber: number | null;
  isGym: boolean;
  isE4: boolean;
  isChampion: boolean;
  mapName: string;
}

export function detectBossFromMap(mapName?: string | null, badgeCount: number = 0): DetectedBoss | null {
  if (!mapName) return null;
  const match = GYM_MAP_TO_LEADER[mapName];
  if (!match) return null;

  if (match.isChampion) {
    return {
      trainerKey: 'STEVEN',
      displayName: match.displayName,
      stage: 'champion',
      gymNumber: null,
      isGym: false,
      isE4: false,
      isChampion: true,
      mapName,
    };
  }

  if (match.isE4) {
    return {
      trainerKey: match.trainerKey,
      displayName: match.displayName,
      stage: 'e4',
      gymNumber: null,
      isGym: false,
      isE4: true,
      isChampion: false,
      mapName,
    };
  }

  // Gym calculation: Gym version = Badges + 1 (capped at 8)
  const gymNumber = Math.min(Math.max(badgeCount + 1, 1), 8);
  const stage = `gym-${gymNumber}`;

  return {
    trainerKey: match.trainerKey,
    displayName: match.displayName,
    stage,
    gymNumber,
    isGym: true,
    isE4: false,
    isChampion: false,
    mapName,
  };
}

let cachedGymsText: string | null = null;
let cachedTrainers: TrainerData[] | null = null;

export async function fetchAndParseGyms(): Promise<TrainerData[]> {
  if (cachedTrainers) return cachedTrainers;

  if (!cachedGymsText) {
    const res = await fetch('/gyms.txt');
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const text = await res.text();
    if (text.trim().startsWith('<!DOCTYPE html>') || text.trim().startsWith('<html')) {
      throw new Error('gyms.txt returned HTML');
    }
    cachedGymsText = text;
  }

  cachedTrainers = parseGymsText(cachedGymsText);
  return cachedTrainers;
}

export function parseGymsText(gymsText: string): TrainerData[] {
  const sections = gymsText.split('=== TRAINER_').slice(1);
  const trainers: TrainerData[] = [];

  sections.forEach((section, index) => {
    const lines = section.split('\n');
    let trainerIdLine = lines[0].trim().replace(/_/g, ' ').replace(/===/g, '').trim();

    if (trainerIdLine.startsWith('JUAN')) {
      trainerIdLine = trainerIdLine.replace('JUAN', 'Juan and Wallace');
    } else {
      trainerIdLine = trainerIdLine.split(' ').map(word => {
        if (/^[A-Z&]+$/.test(word)) {
          if (word.toLowerCase() === 'and') return 'and';
          return word.charAt(0) + word.slice(1).toLowerCase();
        }
        return word;
      }).join(' ');
    }

    const nameLine = lines.find(line => line.trim().startsWith('Name:'));
    let trainerKey = '';
    let stage = '';
    let color: string | undefined = undefined;

    const trainerIdUpper = lines[0].trim().replace(/_/g, ' ').replace(/===/g, '').trim().toUpperCase();
    const idMatch = trainerIdUpper.match(/^(.*?)(?:\s+(\d+))?$/);
    const rawBaseName = idMatch ? idMatch[1] : '';
    const gymNum = idMatch?.[2] || '';

    if (nameLine) {
      const rawName = nameLine.split(':')[1].trim();
      trainerKey = rawName.replace(/ & /g, '&').toUpperCase();

      if (trainerTypeColors[trainerKey]) {
        color = trainerTypeColors[trainerKey];
      }

      let stageBaseName = rawBaseName;
      if (stageBaseName === 'JUAN') stageBaseName = 'JUAN AND WALLACE';
      if (gymLeaders.includes(stageBaseName)) {
        stage = gymNum ? `gym-${gymNum}` : 'gym';
      } else if (e4Members.includes(trainerKey)) {
        stage = 'e4';
      } else if (['ARCHIE', 'MAXIE'].includes(trainerKey)) {
        stage = 'gym-8';
      } else if (trainerKey === 'STEVEN' || trainerKey === 'WALLY') {
        stage = 'champion';
      }
    }

    const pokemonList: PokemonData[] = [];
    let trainerItems = '';
    let currentPokemon: Partial<PokemonData> | null = null;
    const prefixesToIgnore = ['Class:', 'Pic:', 'Gender:', 'Music:', 'Double Battle:', 'IVs:', 'Nature:', 'AI:', 'Mugshot:'];
    const nonPokemonPrefixes = ['Name:', 'Items:', 'Level:', 'Ability:', '-'];

    for (const line of lines.slice(1)) {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('Items:')) {
        trainerItems = trimmedLine.substring(6).trim();
        continue;
      }

      if (trimmedLine === '' || trimmedLine.startsWith('Name:') || prefixesToIgnore.some(p => trimmedLine.startsWith(p))) {
        if (trimmedLine === '' && currentPokemon) {
          pokemonList.push(currentPokemon as PokemonData);
          currentPokemon = null;
        }
        continue;
      }

      const isPokemonLine = trimmedLine.length > 0 && !nonPokemonPrefixes.some(p => trimmedLine.startsWith(p));

      if (isPokemonLine) {
        if (currentPokemon) pokemonList.push(currentPokemon as PokemonData);

        const [pokemonNamePart, itemPart] = trimmedLine.split('@');
        let pokemonName = pokemonNamePart.trim();
        let gender: 'M' | 'F' | undefined = undefined;

        const genderMatch = pokemonName.match(/^(.*?)\s*\(([FM])\)$/);
        if (genderMatch) {
          pokemonName = genderMatch[1].trim();
          gender = genderMatch[2] as 'M' | 'F';
        }

        const imageName = pokemonName.toLowerCase()
          .replace(/ /g, '-')
          .replace(/[.'']/g, '')
          .replace(/♀/g, '-f')
          .replace(/♂/g, '-m')
          .replace(/-hisui$/, '-hisuian')
          .replace(/-galar$/, '-galarian')
          .replace(/-alola$/, '-alolan');

        currentPokemon = {
          name: pokemonName,
          item: itemPart ? itemPart.trim() : '',
          gender,
          imageUrl: `https://img.pokemondb.net/sprites/home/normal/${imageName}.png`,
          details: [],
          rawDetails: [],
          moves: [],
          ability: '',
          level: '',
        };
      } else if (currentPokemon) {
        currentPokemon.rawDetails?.push(trimmedLine);
        if (trimmedLine.startsWith('-')) {
          const moveName = trimmedLine.substring(1).trim();
          currentPokemon.moves?.push(moveName);
          const moveUrlName = moveName.toLowerCase().replace(/ /g, '-');
          currentPokemon.details?.push(
            <div key={moveName}>- <a href={`https://pokemondb.net/move/${moveUrlName}`} target="_blank" rel="noopener noreferrer" className="move-link">{moveName}</a></div>
          );
        } else if (trimmedLine.startsWith('Ability:')) {
          const abilityName = trimmedLine.split(':')[1].trim();
          currentPokemon.ability = abilityName;
          const abilityUrlName = abilityName.toLowerCase().replace(/ /g, '-');
          currentPokemon.details?.push(
            <div key={abilityName}>Ability: <a href={`https://pokemondb.net/ability/${abilityUrlName}`} target="_blank" rel="noopener noreferrer" className="move-link">{abilityName}</a></div>
          );
        } else if (trimmedLine.startsWith('Level:')) {
          currentPokemon.level = trimmedLine.split(':')[1].trim();
          currentPokemon.details?.push(
            <div key={trimmedLine}>
              {trimmedLine}
              {currentPokemon.gender === 'M' && <span style={{ color: '#6890F0', marginLeft: '5px', fontWeight: 'bold' }}>♂</span>}
              {currentPokemon.gender === 'F' && <span style={{ color: '#EE99AC', marginLeft: '5px', fontWeight: 'bold' }}>♀</span>}
            </div>
          );
        } else {
          currentPokemon.details?.push(<div key={trimmedLine}>{trimmedLine}</div>);
        }
      }
    }
    if (currentPokemon) pokemonList.push(currentPokemon as PokemonData);

    const infoSections: InfoSection[] = [];
    const generalInfo: React.ReactNode[] = [];

    if (rawBaseName.includes('JUAN')) {
      const blitzContent: React.ReactNode[] = [
        <div key="blitz-text"><b>Blitz Mechanic:</b> When Tatsugiri becomes a Commander, he disappears inside the mouth of his ally, raising their Attack, SpAtk, and Speed (but not defense or SpDef).</div>
      ];
      if (['6', '7', '8'].includes(gymNum)) {
        blitzContent.push(
          <div key="heads-up"><b>Heads Up! Tatsugiri can command Dondozo <i>and</i> Whiscash!</b></div>
        );
      }
      infoSections.push({ type: 'blitzMechanic', content: blitzContent });

      if (['1', '2'].includes(gymNum)) {
        infoSections.push({
          type: 'bossTip',
          content: [
            <div key="tip"><b>Boss Tip:</b> Eevee&apos;s Baby-Doll Eyes is a surefire way to outspeed the Dondozo and lower its attack stat. Because Dondozo can&apos;t swap out, lowering its stats is key to making this fight manageable!</div>
          ]
        });
      } else if (['3', '4'].includes(gymNum)) {
        infoSections.push({
          type: 'bossTip',
          content: [
            <div key="tip"><b>Boss Tip:</b> Eevee&apos;s Baby-Doll Eyes is a surefire way to outspeed the Dondozo and lower its attack stat. Because Dondozo can&apos;t swap out once Tatsugiri commands it, lowering its stats is key to making this fight manageable!</div>
          ]
        });
      }
    }

    if (rawBaseName === 'TATE AND LIZA' && ['3', '4'].includes(gymNum)) {
      infoSections.push({
        type: 'bossTip',
        content: [
          <div key="tip"><b>Boss Tip:</b> Solrock and Lunatone are much easier to take on one at a time than together. It&apos;s often best to intentionally leave the Natu or Baltoy alive to prevent both big threats from coming out at once!</div>
        ]
      });
    }

    if (rawBaseName === 'BRAWLY') {
      if (['5', '6'].includes(gymNum)) {
        infoSections.push({
          type: 'bossTip',
          content: [
            <div key="tip"><b>Boss Tip:</b> Sirfetch&apos;d deals big damage! Sirfetch&apos;d&apos;s Leek raises its odds of landing a critical hit by 2 stages. This means each of its moves have a 50% critical hit chance!</div>
          ]
        });
      } else if (gymNum === '7') {
        infoSections.push({
          type: 'bossTip',
          content: [
            <div key="tip"><b>Boss Tip:</b> Sirfetch&apos;d and Blaziken deal big damage! Sirfetch&apos;d&apos;s Leek and Blaziken&apos;s Focus Energy each raise their odds of landing a critical hit by 2 stages. This means that increased crit-chance moves like Blaze Kick have a 100% critical hit rate!</div>
          ]
        });
      } else if (gymNum === '8') {
        infoSections.push({
          type: 'bossTip',
          content: [
            <div key="tip"><b>Boss Tip:</b> Sirfetch&apos;d and Blaziken deal big damage! Sirfetch&apos;d&apos;s Leek and Blaziken&apos;s Focus Energy each raise their odds of landing a critical hit by 2 stages. This means that increased crit-chance moves like Blaze Kick and Leaf Blade have a 100% critical hit rate!</div>
          ]
        });
      }
    }

    if (rawBaseName === 'VIOLA') {
      infoSections.push({
        type: 'bossTip',
        content: [
          <div key="tip"><b>Boss Tip:</b> Viola&apos;s Pokemon can see if your Pokemon are holding berries, which increases her odds of using Bug Bite. You can take advantage of this by equipping your Pokemon with useless Persim or Chesto Berries to &quot;trick&quot; her into using Bug Bite instead of higher damage options.</div>
        ]
      });
    }

    if (trainerKey === 'ARCHIE') {
      generalInfo.push(
        <span key="archie-info">Archie has a 50% chance of appearing instead of Juan and Wallace 8.</span>
      );
    }
    if (trainerKey === 'MAXIE') {
      generalInfo.push(
        <span key="maxie-info">Maxie has a 50% chance of appearing instead of Flannery 8.</span>
      );
    }

    trainers.push({
      id: `trainer-${index}`,
      displayName: trainerIdLine,
      trainerKey,
      stage,
      color,
      items: trainerItems,
      infoSections,
      generalInfo,
      pokemon: pokemonList,
    });
  });

  return trainers;
}
